import { motion } from 'framer-motion';
import { LightBulbIcon, SparklesIcon, TrophyIcon, UserGroupIcon, RocketLaunchIcon, HeartIcon } from '@heroicons/react/24/outline';
import AnimatedSection from '../components/AnimatedSection';
import Counter from "../components/StatsCounter";

const About = () => {
  const values = [
    {
      icon: LightBulbIcon,
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and creative solutions to solve complex challenges and push the boundaries of what\'s possible.'
    },
    {
      icon: SparklesIcon,
      title: 'Elegance',
      description: 'Every pixel, every interaction, and every line of code is crafted with meticulous attention to detail and sophisticated aesthetics.'
    },
    {
      icon: TrophyIcon,
      title: 'Excellence',
      description: 'We set the highest standards for ourselves and our work, delivering solutions that exceed expectations every time.'
    }
  ];

  const team = [
    {
      name: 'Nahom Temsgen',
      role: 'Founder & CEO',
      // image: 'https://images.unsplash.com/photo-1542744095-fcf48d80b0fd',
      image: '/Nahom.jpg',
      bio: 'Visionary leader with 5+ years in tech innovation and digital transformation.',
      skills: ['Strategy', 'Leadership', 'Innovation']
    },
    {
      name: 'Sarah Chen',
      role: 'Lead Developer',
      image: 'https://images.pexels.com/photos/1181312/pexels-photo-1181312.jpeg',
      bio: 'Full-stack expert specializing in modern web technologies and scalable architectures.',
      skills: ['React', 'Node.js', 'Cloud Architecture']
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Design Director',
      image: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg',
      bio: 'Award-winning designer creating beautiful and functional user experiences.',
      skills: ['UI/UX', 'Branding', 'Motion Design']
    },
    {
      name: 'Emily Watson',
      role: 'Project Manager',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3',
      bio: 'Agile expert ensuring smooth project delivery and exceptional client communication.',
      skills: ['Agile', 'Client Relations', 'Quality Assurance']
    }
  ];

  const stats = [
    { number: '150+', label: 'Projects Completed' },
    { number: '50+', label: 'Happy Clients' },
    { number: '5+', label: 'Years Experience' },
    { number: '98%', label: 'Client Satisfaction' }
  ];

  const milestones = [
    {
      year: '2020',
      title: 'CleanTech Founded',
      description: 'Started with a vision to create premium digital experiences that make a difference.'
    },
    {
      year: '2021',
      title: 'First Major Client',
      description: 'Delivered our first enterprise-level project, setting new standards for digital excellence.'
    },
    {
      year: '2022',
      title: 'Award Recognition',
      description: 'Received industry recognition for innovative design and development practices.'
    },
    {
      year: '2023',
      title: 'Team Expansion',
      description: 'Grew our team to include specialists in AI, mobile development, and cloud architecture.'
    },
    {
      year: '2024',
      title: 'Global Reach',
      description: 'Expanded operations to serve clients across North America and Europe.'
    },
    {
      year: '2025',
      title: 'Future Vision',
      description: 'Leading the next generation of digital transformation with AI-powered solutions.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern opacity-30" />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl lg:text-7xl font-bold text-cleantech-white mb-6">
              About <span className="text-gradient">CleanTech</span>
            </h1>
            <p className="text-xl lg:text-2xl text-white/80 max-w-4xl mx-auto">
              We are a premium software company dedicated to crafting innovative digital solutions 
              that elevate brands and drive business success through technology and design excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <AnimatedSection className="py-20">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
                Our <span className="text-gradient">Story</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80">
                <p>
                  CleanTech was born from a passion for luxury and technology. We recognized a gap in the market 
                  for truly premium digital solutions that combine cutting-edge functionality with sophisticated aesthetics.
                </p>
                <p>
                  Our journey began with a simple belief: that every business deserves a digital presence that 
                  reflects their quality and ambition. Since our founding, we've been committed to pushing the 
                  boundaries of what's possible in web development, mobile applications, and digital design.
                </p>
                <p>
                  Today, we work with forward-thinking businesses across various industries, helping them 
                  transform their digital vision into reality through innovative solutions that drive growth 
                  and create lasting impact.
                </p>
              </div>
            </div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
                alt="CleanTech Team"
                className="w-full rounded-2xl shadow-premium"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-cleantech-charcoal/60 to-transparent rounded-2xl" />
              <div className="absolute bottom-6 left-6 text-cleantech-white">
                <div className="text-2xl font-bold">Innovation Meets Excellence</div>
                <div className="text-cleantech-gold">Building the Future, Today</div>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Values Section */}
      <AnimatedSection className="py-20 bg-cleantech-charcoal-light">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Our Core <span className="text-gradient">Values</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              These principles guide everything we do, from the first client meeting to the final product delivery.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  whileHover={{ y: -10 }}
                  className="glass-card p-8 text-center hover:shadow-gold transition-all duration-500 group"
                >
                  <div className="w-20 h-20 bg-gold-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:animate-glow">
                    <IconComponent className="w-10 h-10 text-cleantech-charcoal" />
                  </div>
                  <h3 className="text-2xl font-bold text-cleantech-white mb-4 group-hover:text-cleantech-gold transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-white/80">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* Stats Section
      <AnimatedSection className="py-20">
        <div className="section-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-6xl font-bold text-gradient mb-3">
                  {stat.number}
                </div>
                <div className="text-lg text-white/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection> */}
      {/* Stats Section */}
      <AnimatedSection className="py-20">
        <div className="section-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl lg:text-6xl font-bold text-gradient mb-3">
                  {stat.number === "24/7" ? ( <span>{stat.number}</span>) : (
                    <Counter end={stat.number} />
                 )}
                </div>
                <div className="text-white/70">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Team Section */}
      {/* <AnimatedSection className="py-20 bg-cleantech-charcoal-light">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Meet Our <span className="text-gradient">Team</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Our diverse team of experts brings together years of experience in technology, design, and business strategy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="glass-card overflow-hidden hover:shadow-gold transition-all duration-500 group"
              >
                <div className="relative">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-cleantech-charcoal to-transparent opacity-60" />
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-cleantech-white mb-1">{member.name}</h3>
                  <div className="text-cleantech-gold font-medium mb-3">{member.role}</div>
                  <p className="text-white/80 text-sm mb-4">{member.bio}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {member.skills.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-2 py-1 bg-cleantech-gold/20 text-cleantech-gold rounded text-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection> */}

      {/* Timeline Section */}
      <AnimatedSection className="py-20">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Our <span className="text-gradient">Journey</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              From humble beginnings to industry leadership, here's how we've evolved over the years.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-0.5 bg-cleantech-gold/30 lg:transform lg:-translate-x-1/2" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                    index % 2 === 0 ? '' : 'lg:grid-flow-col-dense'
                  }`}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-4 lg:left-1/2 w-4 h-4 bg-cleantech-gold rounded-full lg:transform lg:-translate-x-1/2 z-10" />
                  
                  {/* Content */}
                  <div className={`pl-12 lg:pl-0 ${index % 2 === 0 ? '' : 'lg:col-start-2 lg:text-right'}`}>
                    <div className="glass-card p-6 hover:shadow-gold transition-all duration-500">
                      <div className="text-3xl font-bold text-gradient mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-cleantech-white mb-3">{milestone.title}</h3>
                      <p className="text-white/80">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Spacer for opposite side */}
                  <div className={`hidden lg:block ${index % 2 === 0 ? 'lg:col-start-2' : 'lg:col-start-1'}`} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Mission Statement */}
      <AnimatedSection className="py-20 bg-cleantech-charcoal-light">
        <div className="section-container">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="glass-card p-12 text-center hover:shadow-gold-lg transition-all duration-500"
          >
            <HeartIcon className="w-16 h-16 text-cleantech-gold mx-auto mb-6" />
            <h2 className="text-4xl lg:text-5xl font-bold text-cleantech-white mb-6">
              Our <span className="text-gradient">Mission</span>
            </h2>
            <p className="text-xl lg:text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed">
              To empower businesses with premium digital solutions that not only meet their immediate needs 
              but also position them for long-term success in an ever-evolving digital landscape. We believe 
              that exceptional technology combined with thoughtful design can transform how businesses connect 
              with their audiences and achieve their goals.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default About;